import { Box, Button, CircularProgress, Typography, useTheme } from '@material-ui/core';
import type { ResumeProcessorProps } from '@mf-types/remote/compiled-types/components/ResumeProcessor/ResumeProcessor';
import {
  Backdrop,
  Checkbox,
  PlusIconTwoTone,
  Select,
  useNotificationModal,
  WarningCircleFilledIcon,
} from '@room-match/shared-ui-components';
import { AxiosResponse } from 'axios';
import AsyncResumeProcessor from 'components/AsyncResumeProcessor/AsyncResumeProcessor';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { COLORS } from 'shared/constants/COLORS';
import { RESUME_DEFAULT_DATA } from 'shared/constants/INITIAL_RESUME';
import { resume_categories } from 'shared/constants/RESUME_CATEGORIES';
import { useAxios } from 'shared/hooks/useAxios';
import { useInvalidateProfile } from 'shared/hooks/useInvalidateProfile';
import { IResumeCredit } from 'shared/interfaces/IResume';
import { ICategory, ICredit, IResume } from 'shared/interfaces/ITalent';
import { IAxiosErrorResponse } from 'shared/interfaces/utils/IAxios';
import { talentService } from 'shared/services/talentService';
import { uploadService } from 'shared/services/uploadService';
import { authToken } from 'shared/utils/authToken';
import { v4 as uuidv4 } from 'uuid';
import { captureMessage } from '../../../lib/sentry';
import { useStyles } from '../Skills/SkillsComponents/Popovers/Popovers.styles';
import CategoryModal from './CategoryModal/CategoryModal';
import {
  AddResumeButton,
  Bold,
  CheckboxContainer,
  Header,
  HeaderRight,
  MainCont,
  Title,
  UploadCreditsButton,
} from './Resume.styles';
import ResumeCategoryItem from './ResumeCategoryItem/ResumeCategoryItem';
import { DragError, useCategoryDragDrop } from './hooks';
import useModal from './useModal';
import useResumeProcessor from './useResumeProcessor';
import extractCategories from './utils/extractCategories';
import mergeCatalogSections from './utils/mergeCatalogSections';

enum ResumeProcessorOption {
  PDF = 'PDF',
  URL = 'URL',
}

const { getResume, updateTalent } = talentService();
const { uploadMedia } = uploadService();
const { POST } = useAxios();
const { getAuthToken } = authToken();

interface IProcessLinkPayload {
  link: string;
  categories: ResumeProcessorProps['categorizedCredits'];
}

interface IProcessLinkResponse {
  data: ResumeProcessorProps['categorizedCredits'];
}

const postProcessLinkToCreditsDao = async (payload: IProcessLinkPayload) => {
  const response = await POST<IProcessLinkResponse>({
    url: `${process.env.REACT_APP_MFE_API}/api/cv-processor`,
    headers: {
      Authorization: `Bearer ${getAuthToken()}`,
    },
    data: payload,
  });
  return response;
};

const postProcessLinkToCredits = () => {
  return useMutation<AxiosResponse<IProcessLinkResponse>, IAxiosErrorResponse, IProcessLinkPayload>(
    ['postProcessLinkToCredits'],
    (payload) => postProcessLinkToCreditsDao(payload),
  );
};

const Resume = () => {
  const classes = useStyles();
  const [selectedOption, setSelectedOption] = useState<string>('');
  const { handleSetMessage, handleOpen, NotificationModal, isOpen } = useNotificationModal({
    type: 'error',
  });
  const queryClient = useQueryClient();
  const theme = useTheme();
  const { invalidateMultipleProfileResume } = useInvalidateProfile();
  const { mutateAsync: uploadMediaMutateAsync, isLoading: uploadMediaMutateLoading } = uploadMedia();
  const {
    mutateAsync: postProcessLinkToCreditsMutateAsync,
    isLoading: postProcessLinkToCreditsMutateLoading,
    data: postProcessLinkToCreditsData,
    isSuccess: postProcessLinkToCreditsSuccess,
  } = postProcessLinkToCredits();
  const { open, openModal, closeModal } = useModal();
  const { setOptionPDF, setOptionURL, option } = useResumeProcessor();
  const { mutateAsync: updateTalentResume, isLoading: updateTalentResumeLoading } = updateTalent();
  const { data: talentResumeData, isLoading, refetch } = getResume();
  const [hideYears, setHideYears] = useState<boolean>(false);
  const [savingYears, setSavingYears] = useState<boolean>(false);
  const [showAdd, setshowAdd] = useState(false);
  const [failedToLoad, setfailedToLoad] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [categorizedCredits, setCategorizedCredits] = useState<ResumeProcessorProps['categorizedCredits']>([]);
  const [isEmptyResult, setIsEmptyResult] = useState(false);
  const preparedCategorizedCredits = useMemo(() => categorizedCredits, [categorizedCredits]);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  // Initialize drag and drop hook
  const {
    categories: resume,
    onDragEnd,
    updateCategories: setresume,
    setCategoriesDirectly,
    resetError,
    dragError,
    DragDropProvider,
    CategoryDroppable,
    CategoryDraggable,
    getDroppableStyle,
    getDraggableStyle,
  } = useCategoryDragDrop([], {
    enableCrossCategoryMoves: true,
    enableValidation: true,
    onError: (error: DragError) => {
      // Log internal drag errors
      console.error('Drag Error:', error);
      captureMessage({
        message: `Drag error: ${error.type}`,
        data: { error },
      });
    },
    onDataChange: (newCategories: ICategory[]) => {
      // Handle data changes from drag operations
      // Only save to API, don't update local state (hook already handles this)
      saveResumeToAPI(newCategories);
    },
  });

  useEffect(() => {
    if (postProcessLinkToCreditsSuccess && postProcessLinkToCreditsData) {
      if (postProcessLinkToCreditsData.data?.result?.resume.length !== 0) {
        setCategorizedCredits(
          postProcessLinkToCreditsData.data?.result?.resume as ResumeProcessorProps['categorizedCredits'],
        );
      }
      if (postProcessLinkToCreditsData.data?.result?.empty_result) {
        setIsEmptyResult(true);
      }
    }
  }, [postProcessLinkToCreditsSuccess, postProcessLinkToCreditsData]);

  useEffect(() => {
    if (!talentResumeData) return;

    const resumesData: IResume['attributes']['resume']['categories'] =
      talentResumeData?.data.attributes.resume?.categories || [];
    const hideYears: IResume['attributes']['resume']['hide_years'] =
      talentResumeData?.data.attributes.resume?.hide_years || false;

    setHideYears(hideYears);
    setCategoriesDirectly([...resumesData]);
    if (resumesData.length === 0 && !isLoading) {
      setfailedToLoad(true);
      captureMessage('user failed to load resume data');
    }
  }, [talentResumeData, setCategoriesDirectly]);

  // API-only save function for drag operations
  const saveResumeToAPI = async (data: ICategory[]) => {
    try {
      setIsUpdating(true);
      setApiError(null);
      resetError();

      await updateTalentResume(
        {
          resume: {
            categories: data,
            hide_years: hideYears,
          },
        },
        {
          onSuccess: () => {
            // Don't update local state - hook already has the updated data
            // HIGH PRIORITY CHANGE: Add profile invalidation
            invalidateMultipleProfileResume();
            refetch();
          },
          onError: (error) => {
            setApiError('Failed to save changes. Please try again.');
            captureMessage({
              message: 'Resume update failed',
              data: { error, data },
            });
            // Revert to previous state
            refetch();
          },
        },
      );
    } catch (error) {
      console.error('Resume update error:', error);
      setApiError('An unexpected error occurred. Please refresh the page and try again.');
      captureMessage({
        message: 'Resume update exception',
        data: { error, data },
      });
    } finally {
      setIsUpdating(false);
      setDeleting(false);
    }
  };

  const resumeUpdater = async (data: ICategory[]) => {
    try {
      setIsUpdating(true);
      setApiError(null);
      resetError();

      await updateTalentResume(
        {
          resume: {
            categories: data,
            hide_years: hideYears,
          },
        },
        {
          onSuccess: () => {
            // Use setCategoriesDirectly for API response sync - doesn't trigger onDataChange again
            setCategoriesDirectly([...data]);
            // HIGH PRIORITY CHANGE: Add profile invalidation
            invalidateMultipleProfileResume();
            refetch();
          },
          onError: (error) => {
            setApiError('Failed to save changes. Please try again.');
            captureMessage({
              message: 'Resume update failed',
              data: { error, data },
            });
            // Revert to previous state
            refetch();
          },
        },
      );
    } catch (error) {
      console.error('Resume update error:', error);
      setApiError('An unexpected error occurred. Please refresh the page and try again.');
      captureMessage({
        message: 'Resume update exception',
        data: { error, data },
      });
    } finally {
      setIsUpdating(false);
      setDeleting(false);
    }
  };

  const resumeCreditUpdater = (data: IResumeCredit, categoryID: string) => {
    const updatedData = resume.map((m) => {
      if (m.category_id === categoryID) {
        return {
          ...m,
          credits: [{ ...data }, ...m.credits],
        };
      }
      return m;
    });

    resumeUpdater(updatedData);
  };

  const modifyCredits = (data: ICredit[], categoryID: string) => {
    const updatedData = resume.map((m) => {
      if (m.category_id === categoryID) {
        return {
          ...m,
          credits: data,
        };
      }
      return m;
    });

    resumeUpdater(updatedData);
  };

  const handleChange = () => {
    setHideYears(!hideYears);
    setSavingYears(true);
    updateTalentResume(
      {
        resume: {
          categories: resume,
          hide_years: !hideYears,
        },
      },
      {
        onSuccess: (datasss) => {
          setSavingYears(false);
        },
      },
    );
  };

  const handleSelect = async (e: string) => {
    const newData = {
      category: e,
      category_id: uuidv4(),
      credits: [],
    };
    setresume([newData, ...resume]);
    await resumeUpdater([newData, ...resume]);
    setshowAdd(false);
  };

  const handleDeleteCategory = (id: string) => {
    setDeleting(true);
    const modifiedData = resume.filter((f) => f.category_id !== id);
    setresume(modifiedData);
    resumeUpdater(modifiedData);
  };

  const onPDFClick = () => {
    setOptionPDF();
    openModal();
  };

  const onURLClick = () => {
    setOptionURL();
    openModal();
  };

  const handleUploadPDF = async (file: File) => {
    try {
      setIsEmptyResult(false);
      const categories = extractCategories(talentResumeData);
      // upload to s3
      const formData = new FormData();
      formData.set('attachment', file);
      formData.append('file_name', file.name);
      formData.append('file_type', 'document');
      formData.set('file_size', file.size.toString());
      formData.append('media_bin', 'true');

      const uploadedPhoto = await uploadMediaMutateAsync({
        formData,
        onProgres: (progress: number) => {
          setUploadProgress(progress);
        },
      });
      // send post request to postman.
      const payload = {
        link: uploadedPhoto.data.attributes.attachment_url,
        categories,
      };
      await postProcessLinkToCreditsMutateAsync(payload, {
        onSuccess: () => {
          setUploadProgress(0);
        },
        onError: () => {
          handleSetMessage('Failed Processing PDF!', 'Something went wrong, please try again.', 'OK');
          handleOpen();
          setUploadProgress(0);
        },
      });
    } catch (e) {
      handleSetMessage('Failed Processing PDF!', 'Something went wrong, please try again.', 'OK');
      handleOpen();
      setUploadProgress(0);
    }
  };

  const handleUploadURL = async (url: string) => {
    const categories = extractCategories(talentResumeData);
    // send post request to postman.
    const payload = {
      link: url,
      categories,
    };
    await postProcessLinkToCreditsMutateAsync(payload, {
      onError: () => {
        closeModal();
        handleSetMessage('Failed Processing URL!', 'Something went wrong, please try again.', 'OK');
        handleOpen();
      },
    });
  };

  const handleOnSave = async (categorizedCredits: ResumeProcessorProps['categorizedCredits']) => {
    const mergeData = mergeCatalogSections(
      talentResumeData?.data.attributes.resume.categories as ResumeProcessorProps['categorizedCredits'],
      categorizedCredits,
      'category',
    );

    await updateTalentResume(
      {
        resume: {
          categories: mergeData,
          hide_years: hideYears,
        },
      },
      {
        onSuccess: () => {
          refetch();
          closeModal();
          queryClient.invalidateQueries({ queryKey: ['postProcessLinkToCredits'] });
          setCategorizedCredits([]);
        },
        onError: () => {
          closeModal();
          handleSetMessage('Already Submitted!', 'You have already submitted your application for this role', 'OK');
          handleOpen();
        },
      },
    );
  };

  const shouldDisable = useMemo(() => {
    return postProcessLinkToCreditsMutateLoading || uploadMediaMutateLoading || updateTalentResumeLoading;
  }, [postProcessLinkToCreditsMutateLoading, uploadMediaMutateLoading, updateTalentResumeLoading]);

  const handleOnCloseModal = useCallback(() => {
    if (shouldDisable) return;
    setIsEmptyResult(false);
    closeModal();
    setTimeout(() => {
      setSelectedOption('');
      setCategorizedCredits([]);
    }, 1000);
  }, [shouldDisable, closeModal]);

  const actionPlaceholder = useMemo(() => {
    if (postProcessLinkToCreditsMutateLoading || uploadMediaMutateLoading) {
      if (uploadProgress === 100) {
        return 'Analyzing PDF';
      }
      return `${uploadProgress.toFixed(0)}% Uploading`;
    } else if (updateTalentResumeLoading) {
      return 'Saving';
    } else {
      return null;
    }
  }, [postProcessLinkToCreditsMutateLoading, uploadMediaMutateLoading, updateTalentResumeLoading, uploadProgress]);

  const handleOnCloseNotificationModal = () => {
    openModal();
  };

  const onClickClearCredits = async () => {
    const mergeData = mergeCatalogSections(
      talentResumeData?.data.attributes.resume.categories as ResumeProcessorProps['categorizedCredits'],
      categorizedCredits,
      'category',
    );
    await updateTalentResume(
      {
        resume: {
          categories: [],
          hide_years: hideYears,
        },
      },
      {
        onSuccess: () => {
          refetch();
          closeModal();
          queryClient.invalidateQueries({ queryKey: ['postProcessLinkToCredits'] });
          setCategorizedCredits([]);
        },
      },
    );
  };

  // Focus trap conflict prevention - ensure only one modal is open at a time
  useEffect(() => {
    // Close CategoryModal when ResumeProcessor opens
    if (open && showAdd) {
      setshowAdd(false);
    }
  }, [open, showAdd]);

  useEffect(() => {
    // Close CategoryModal when NotificationModal opens
    if (isOpen && showAdd) {
      setshowAdd(false);
    }
  }, [isOpen, showAdd]);

  useEffect(() => {
    // Close NotificationModal when ResumeProcessor opens
    if (open && isOpen) {
      // Note: NotificationModal close is handled by handleSetMessage/handleOpen
      // This is just for reference - the render conditions above handle the display
    }
  }, [open, isOpen]);

  const onHandleSelectOptionChange = (e: any) => {
    setSelectedOption(e.target.value);
    if (e.target.value === 'PDF') {
      onPDFClick();
    }
    if (e.target.value === 'URL') {
      onURLClick();
    }
    if (e.target.value === 'CLEAN_CREDITS') {
      onClickClearCredits();
    }
  };

  const initializeResume = () => {
    setIsInitializing(true);
    updateTalentResume(
      {
        resume: RESUME_DEFAULT_DATA,
      },
      {
        onSuccess: () => {
          setIsInitializing(false);
          setresume(RESUME_DEFAULT_DATA.categories);
        },
      },
    );
  };

  return resume.length === 0 ? (
    isLoading && !failedToLoad ? (
      <Backdrop isLoading={isLoading} />
    ) : (
      <div
        style={{
          minHeight: 140,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 40,
          padding: 40,
        }}
      >
        <WarningCircleFilledIcon style={{ width: 50, height: 50 }} />
        <p style={{ maxWidth: 500, textAlign: 'center', fontWeight: 700, fontSize: 20 }}>
          Your Resume Data failed to load
        </p>

        <p style={{ maxWidth: 650, textAlign: 'center', fontSize: 18 }}>
          Please hold down the SHIFT key on your computer and select reload in your browser, or reload the page if you
          are on mobile.{' '}
          <Link
            to={'/talent/my_info/resume'}
            onClick={() => window.location.reload()}
            style={{ textDecoration: 'none' }}
          >
            <Bold> Reload the Page</Bold>
          </Link>
        </p>

        <p style={{ maxWidth: 650, textAlign: 'center', fontSize: 18 }}>
          If this is your <Bold>first visit to the Resume Page</Bold>, and you haven't added any credits yet, you can
          click the button below to create a blank resume.
        </p>
        <Typography style={{ maxWidth: 650, textAlign: 'center', marginBottom: -40 }}>
          <Bold style={{ color: COLORS.ERROR }}>IMPORTANT: </Bold>
        </Typography>
        <Typography style={{ maxWidth: 650, textAlign: 'center', color: COLORS.ERROR }}>
          Do not click this button if you have previously added credits to your resume as it will erase them.
        </Typography>

        <Button disabled={isInitializing} onClick={initializeResume} variant="contained" color="primary" size="large">
          {isInitializing ? <CircularProgress size={18} /> : 'CREATE A NEW BLANK RESUME'}
        </Button>
      </div>
    )
  ) : (
    <>
      <MainCont>
        <Header>
          <Title>Resume</Title>
          <HeaderRight>
            {/* Drag Error Display */}
            {dragError && (
              <Box
                style={{
                  backgroundColor: COLORS.ERROR,
                  color: COLORS.WHITE,
                  padding: '8px 12px',
                  borderRadius: 4,
                  fontSize: 14,
                  marginRight: 10,
                  maxWidth: 300,
                }}
              >
                {dragError.userMessage || dragError.message}
                <Button
                  onClick={resetError}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: COLORS.WHITE,
                    marginLeft: 8,
                    cursor: 'pointer',
                    fontSize: 16,
                  }}
                >
                  ×
                </Button>
              </Box>
            )}
            {/* API Error Display */}
            {apiError && (
              <Box
                style={{
                  backgroundColor: COLORS.ERROR,
                  color: COLORS.WHITE,
                  padding: '8px 12px',
                  borderRadius: 4,
                  fontSize: 14,
                  marginRight: 10,
                  maxWidth: 300,
                }}
              >
                {apiError}
                <Button
                  onClick={() => setApiError(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: COLORS.WHITE,
                    marginLeft: 8,
                    cursor: 'pointer',
                    fontSize: 16,
                  }}
                >
                  ×
                </Button>
              </Box>
            )}
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                marginRight: 10,
                color: COLORS.PRIMARY_TEXT,
                fontSize: 14,
                opacity: isUpdating ? 1 : 0,
                transition: 'opacity 0.3s ease-in-out',
              }}
            >
              <CircularProgress size={16} style={{ marginRight: 8 }} />
              Saving...
            </Box>
            <Box style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
              {savingYears ? (
                <CircularProgress style={{ width: 20, height: 20, marginRight: 10 }} />
              ) : (
                <CheckboxContainer>
                  <Checkbox onChange={handleChange} checked={hideYears} value={hideYears} label="Hide&nbsp;Years" />
                </CheckboxContainer>
              )}
            </Box>
            <Box
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '10px',
                width: '100%',
              }}
            >
              <AddResumeButton
                onClick={() => setshowAdd(true)}
                fullWidth
                disabled={resume.length >= (resume_categories?.length || 12)}
              >
                <PlusIconTwoTone style={{ marginRight: 5 }} />
                <Typography style={{ fontSize: 14 }}>Add Resume Category</Typography>
              </AddResumeButton>
              <UploadCreditsButton
                disabled={preparedCategorizedCredits.length !== 0}
                onClick={() => onPDFClick()}
                fullWidth
              >
                <PlusIconTwoTone style={{ marginRight: 5 }} />
                <Typography style={{ fontSize: 14 }}>Upload Credits</Typography>
              </UploadCreditsButton>
            </Box>
            <Box
              style={{
                width: 170,
                display: 'none',
              }}
            >
              <Select
                className={classes.select}
                variant="outlined"
                placeHolder={'Upload Credits'}
                fullWidth
                data={[
                  { key: 'by PDF', value: 'PDF' },
                  {
                    key: 'Clean Credits',
                    value: 'CLEAN_CREDITS',
                  },
                ]}
                value={selectedOption}
                onChange={onHandleSelectOptionChange}
              />
            </Box>
          </HeaderRight>
        </Header>
        <Box style={{ minHeight: '73vh', padding: '0 20px' }}>
          <DragDropProvider onDragEnd={onDragEnd}>
            <CategoryDroppable
              droppableId="categories"
              type="CATEGORY"
              style={getDroppableStyle({
                theme: theme.palette.type as 'light' | 'dark',
              })}
            >
              {(provided, snapshot) => (
                <Box
                  style={getDroppableStyle({
                    isDraggingOver: snapshot.isDraggingOver,
                    theme: theme.palette.type as 'light' | 'dark',
                  })}
                >
                  {resume?.length ? (
                    resume.map((item: ICategory, index: number) => (
                      <CategoryDraggable
                        key={item.category_id}
                        draggableId={item.category_id || `${item.category + index + item.category_id}`}
                        index={index}
                        style={getDraggableStyle({
                          customStyles: {
                            marginBottom: '20px',
                            display: 'block',
                          },
                        })}
                      >
                        {(provided, snapshot, dragHandleProps) => (
                          <Box
                            style={getDraggableStyle({
                              isDragging: snapshot.isDragging,
                            })}
                          >
                            <ResumeCategoryItem
                              deleting={deleting}
                              ifLastItem={resume.length > 1 ? false : true}
                              deleteCategory={() => handleDeleteCategory(item.category_id)}
                              hideYears={hideYears}
                              modifyCredits={(data: ICredit[]) => modifyCredits(data, item.category_id)}
                              addCredit={(data: IResumeCredit) => resumeCreditUpdater(data, item.category_id)}
                              item={item}
                              key={item.category_id}
                              dragHandleProps={dragHandleProps}
                            />
                          </Box>
                        )}
                      </CategoryDraggable>
                    ))
                  ) : (
                    <></>
                  )}
                </Box>
              )}
            </CategoryDroppable>
          </DragDropProvider>
        </Box>
      </MainCont>
      {/* Only render CategoryModal if it's the only modal that should be open */}
      {showAdd && !isOpen && !open && (
        <CategoryModal
          show={showAdd}
          close={() => setshowAdd(false)}
          onselect={handleSelect}
          added={resume.map((m) => m.category) as string[]}
        />
      )}
      {/* Only render NotificationModal if other modals are not open */}
      {isOpen && !open && !showAdd ? <NotificationModal onClose={handleOnCloseNotificationModal} /> : null}
      {/* AsyncResumeProcessor has highest priority - close other modals when it opens */}
      <AsyncResumeProcessor
        isEmptyResult={isEmptyResult}
        disabled={shouldDisable}
        actionPlaceholder={actionPlaceholder}
        open={open}
        theme={theme.palette.type}
        handleClose={handleOnCloseModal}
        option={option}
        onUploadPDF={handleUploadPDF}
        onUploadURL={handleUploadURL}
        categorizedCredits={preparedCategorizedCredits}
        onSave={handleOnSave}
      />
    </>
  );
};

export default Resume;
export { ResumeProcessorOption };
